import React, { useState } from "react";
import { Col, Row, Input, Button, InputGroup } from "reactstrap";
import makeBlockie from "ethereum-blockies-base64";
import SearchInput from "../../../components/SearchInput/SearchInput.js";
import RemoteTable from "../../../components/Table/RemoteTable.js";
import Loader from "../../../components/Loader/Loader.js";
import Value from "../../../components/Value/Value.js";
import Address from "../../../components/Address/Address.js";
import ExternalLink from "../../../components/ExternalLink/ExternalLink.js";
import { withErrorBoundary } from "../../../hoc.js";
import { useFetch, useQueryParams, useSmartNavigate } from "../../../hooks.js";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import TitleWithTooltip from "../../../components/TitleWithTooltip/TitleWithTooltip.js";
function LeaderBoardTable(props) {
  const smartNavigate = useSmartNavigate();
  const [fullyDilutedMarketCap, setFullyDilutedMarketCap] = useState(0);
  const [sparkTokenValue, setSparkTokenValue] = useState(0);
  const queryParams = useQueryParams();
  const order = queryParams.get("order") || "-airdrop_total_share";
  const searchText = queryParams.get("search");
  const pageSize = 25;
  const page = parseInt(queryParams.get("page")) || 1;
  const parseValue = (input) => {
    const regex = /^(\d+(?:\.\d+)?)(k|m|b)?$/i;
    const match = input.toLowerCase().match(regex);
    if (match) {
      const value = parseFloat(match[1]);
      const suffix = match[2];
      switch (suffix) {
        case "k":
          return value * 1e3;
        case "m":
          return value * 1e6;
        case "b":
          return value * 1e9;
        default:
          return value;
      }
    }
    return NaN; // Return NaN if the format is incorrect
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    // Allow numbers, decimal points, and specific suffixes (k, m, b)
    const validInput = input.replace(/[^0-9.kmbKMB]/g, "");
    setFullyDilutedMarketCap(validInput);
  };
  const { data, isLoading, isPreviousData, isError, ErrorFallbackComponent } = useFetch(
    "airdrop/leaderboard/",
    {
      p: page,
      p_size: pageSize,
      order,
      search: searchText,
      skpToken: sparkTokenValue,
    },
    { keepPreviousData: true }
  );

  if (isLoading) {
    return <Loader />;
  } else if (isError) {
    return <ErrorFallbackComponent />;
  }
  const handleSubmit = () => {
    const numericValue = parseValue(fullyDilutedMarketCap);
    if (!isNaN(numericValue)) {
      const value = numericValue / 1_500_000_000;
      setSparkTokenValue(value.toFixed(2));
    } else {
      setSparkTokenValue(0);
    }
  };
  const onRowClick = (e, row) => {
    smartNavigate(e, `wallets/${row.wallet_address}/`);
  };
  const { results } = data;
  const forumLink = (
    <>
      <ExternalLink href="https://forum.makerdao.com/t/proposed-spark-pre-farming-airdrop-formula/21786">
        MakerDAO forum post
      </ExternalLink>
    </>
  );

  return (
    <>
      <div className="mb-4 text-grey">
        Welcome to the Spark Airdrop Leaderboard! This page displays the current
        standings for the proposed Spark Pre-Farming Airdrop, based on our estimated end
        block of 19912813 (May 20, 2024, at 14:25:04 UTC). Please note that this block
        number is an estimation and may vary. For detailed information and ongoing
        discussions about the airdrop formula and parameters, visit the {forumLink}.
        Stay tuned for any updates or changes as the airdrop progresses.{" "}
      </div>
      <Row className="mb-4">
        <Col>
          <ToolkitProvider
            bootstrap4
            search
            keyField="wallet_address"
            data={results}
            columns={[
              {
                dataField: "",
                text: "",
                formatter: (_, row) => {
                  const blockie = makeBlockie(row.wallet_address);
                  return <img src={blockie} alt={row.wallet_address} />;
                },
                classes: "blockie",
              },
              {
                dataField: "wallet_address",
                text: "Address",
                formatter: (cell) => <Address value={cell} short />,
              },
              {
                dataField: "token_reward_dai",
                text: "DAI",
                formatter: (cell, row) => (
                  <>
                    <Value value={cell} decimals={2} suffix="SPK" compact dashIfZero />
                    <br />
                    <Value
                      value={row.token_reward_dai_usd}
                      decimals={2}
                      prefix={"$"}
                      hideIfZero
                      compact
                      small
                      className="gray"
                    />
                  </>
                ),
                sort: true,
                headerAlign: "right",
                align: "right",
              },
              {
                dataField: "token_reward_eth",
                text: "ETH",
                formatter: (cell, row) => (
                  <>
                    <Value value={cell} decimals={2} suffix="SPK" compact dashIfZero />
                    <br />
                    <Value
                      value={row.token_reward_eth_usd}
                      decimals={2}
                      prefix={"$"}
                      hideIfZero
                      compact
                      small
                      className="gray"
                    />
                  </>
                ),
                sort: true,
                headerAlign: "right",
                align: "right",
              },
              {
                dataField: "airdrop_total_share",
                text: "current total",
                formatter: (cell, row) => (
                  <>
                    <Value
                      value={
                        Number(row.token_reward_dai) + Number(row.token_reward_eth)
                      }
                      decimals={2}
                      suffix={"SPK"}
                      hideIfZero
                      compact
                    />
                    <br />
                    <Value
                      value={
                        Number(row.token_reward_dai_usd) +
                        Number(row.token_reward_eth_usd)
                      }
                      decimals={2}
                      prefix={"$"}
                      compact
                      small
                      className="gray"
                      hideIfZero
                    />
                  </>
                ),
                sort: true,
                headerAlign: "right",
                align: "right",
              },
              {
                dataField: "token_reward_projected",
                text: (
                  <TitleWithTooltip
                    title="projected total"
                    tooltipValue={`This is the SPK token amount this account will have if all positions stay the same size from now until the end of the airdrop.`}
                    identifier={"total-projected"}
                  />
                ),
                formatter: (cell, row) => (
                  <>
                    <Value value={cell} decimals={2} suffix={"SPK"} compact />
                    <br />
                    <Value
                      value={row.token_reward_projected_usd}
                      decimals={2}
                      prefix={"$"}
                      hideIfZero
                      compact
                      small
                      className="gray"
                    />
                  </>
                ),
                sort: true,
                headerAlign: "right",
                align: "right",
              },
            ]}
            bordered={false}
          >
            {(props) => (
              <div>
                <div className="react-bootstrap-table-search  justify-content-between d-flex align-items-center flex-wrap">
                  <div className="mb-3">
                    <label>
                      <TitleWithTooltip
                        title="Year 4 SPK Market Cap"
                        tooltipValue={`Enter the projected market capitalization for the Spark (SPK) token in its fourth year to calculate the price 
                        per SPK token. A total of 1.5 billion SPK tokens will be issued by this time. 
                        The SPK token values shown in the airdrop recipients' table will be determined based on the market cap you provide.`}
                      />
                    </label>
                    <InputGroup>
                      <Input
                        id="fullyDilutedMarketCap"
                        type="text"
                        value={`$${fullyDilutedMarketCap}`} // Display with dollar sign
                        onChange={handleInputChange}
                        placeholder="Enter market cap"
                      />
                      <Button color="secondary" onClick={handleSubmit} className="ml-2">
                        Calculate
                      </Button>
                    </InputGroup>
                  </div>

                  {/* Display the calculated Spark token value */}
                  {sparkTokenValue !== null && (
                    <div className="spark-token-value">
                      <strong>SPK token valuation:</strong> ${sparkTokenValue}
                    </div>
                  )}
                  <div>
                    <SearchInput
                      placeholder="Search by address..."
                      initialSearchText={searchText}
                      {...props.searchProps}
                    />
                  </div>
                </div>
                <RemoteTable
                  {...props.baseProps}
                  onRowClick={onRowClick}
                  loading={isPreviousData}
                  page={page}
                  pageSize={pageSize}
                  totalPageSize={data.count}
                />
              </div>
            )}
          </ToolkitProvider>
        </Col>
      </Row>
    </>
  );
}

export default withErrorBoundary(LeaderBoardTable);
